<template>
    <div class="rich-text-editor-container">
        <div ref="editor" class="quill-editor"></div>
        <div class="char-counter">
            Caracteres: {{ charCount }}
            <span v-if="charCount > 10000" class="info">
                (Se dividirá automáticamente en la base de datos)
            </span>
        </div>
    </div>
</template>
<script>
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const InlineStyle = Quill.import('blots/inline');

class StyleBlot extends InlineStyle {
    static create(value) {
        const node = super.create(value);
        if (value && typeof value === 'object') {
            Object.keys(value).forEach(style => {
                node.style.setProperty(style, value[style]);
            });
        }
        return node;
    }
    static formats(node) {
        const styles = {};
        for (let i = 0; i < node.style.length; i++) {
            const property = node.style[i];
            styles[property] = node.style.getPropertyValue(property);
        }
        return styles;
    }
}
StyleBlot.blotName = 'style';
StyleBlot.tagName = 'span';
Quill.register(StyleBlot);

const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = [
    '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'
];
Quill.register(SizeStyle, true);

export default {
    name: 'RichTextEditor',
    props: {
        value: {
            type: String,
            default: ''
        },
        height: {
            type: Number,
            default: 400
        },
        placeholder: {
            type: String,
            default: 'Escribe el contenido aquí...'
        },
        useInlineStyles: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            quill: null,
            content: '',
            charCount: 0,
            defaultFontSize: '14px',
            currentFontSize: '14px',
            isDestroyed: false,
            styleElement: null
        };
    },
    watch: {
        value(newVal) {
            if (!this.quill || this.isDestroyed) return;
            if (newVal !== this.content) {
                this.setContent(newVal);
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            setTimeout(() => {
                if (this.isDestroyed) return;
                this.initQuill();
                this.setupCSSStyles();
            }, 50);
        });
    },
    beforeDestroy() {
        this.isDestroyed = true;

        if (this.quill) {
            try {
                this.quill.off('text-change');
                this.quill.off('selection-change');
                this.quill.off('editor-change');
            } catch (e) {
                // Ignorar errores al remover listeners
            }

            try {
                this.quill = null;
            } catch (e) {
                // Ignorar errores al limpiar Quill
            }
        }

        if (this.styleElement && this.styleElement.parentNode) {
            this.styleElement.parentNode.removeChild(this.styleElement);
            this.styleElement = null;
        }
    },
    methods: {
        initQuill() {
            if (!this.$refs.editor) return;

            const toolbarOptions = [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'font': [] }],
                [{ 'size': SizeStyle.whitelist }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'align': [] }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'script': 'sub' }, { 'script': 'super' }],
                ['blockquote', 'code-block'],
                ['link', 'image'],
                ['clean']
            ];

            this.quill = new Quill(this.$refs.editor, {
                theme: 'snow',
                placeholder: this.placeholder,
                modules: {
                    toolbar: toolbarOptions,
                    clipboard: {
                        matchVisual: false,
                        allowed: {
                            tags: ['a', 'b', 'strong', 'u', 's', 'i', 'em', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                            attributes: ['style', 'class', 'href', 'target']
                        }
                    }
                }
            });

            this.quill.container.style.height = `${this.height}px`;

            if (this.useInlineStyles) {
                this.enableInlineStyles();
            }

            if (this.value) {
                this.setContent(this.value);
            } else {
                this.applyDefaultStyles();
            }

            this.quill.on('text-change', () => {
                if (this.isDestroyed) return;
                this.updateContent();
            });

            this.quill.on('selection-change', () => {
                if (this.isDestroyed || !this.quill) return;
                this.safeUpdateFontSizeSelector();
            });

            this.quill.on('editor-change', () => {
                if (this.isDestroyed || !this.quill) return;
                this.updateCharCount();
            });

            this.interceptFormatOperations();

            this.$nextTick(() => {
                if (!this.isDestroyed) {
                    this.safeUpdateFontSizeSelector();
                }
            });
        },

        safeUpdateFontSizeSelector() {
            if (!this.quill || this.isDestroyed) return;
            try {
                // Verificar que el editor tenga contenido antes de pedir selección
                if (this.quill.getLength() <= 1) return;
                const range = this.quill.getSelection();
                if (!range) return;
                this.updateFontSizeSelector();
            } catch (e) {
                // Silenciar error de Quill cuando el DOM aún no está listo
                console.warn('[RichTextEditor] selection-change ignorado:', e?.message);
            }
        },

        setupCSSStyles() {
            if (this.isDestroyed) return;
            const style = document.createElement('style');
            style.textContent = `
                .quill-editor .ql-editor * {
                    font-family: inherit;
                }
                .quill-editor .ql-editor strong,
                .quill-editor .ql-editor b {
                    font-weight: bold !important;
                }
                .quill-editor .ql-editor em,
                .quill-editor .ql-editor i {
                    font-style: italic !important;
                }
                .quill-editor .ql-editor u {
                    text-decoration: underline !important;
                }
                .quill-editor .ql-editor s,
                .quill-editor .ql-editor strike {
                    text-decoration: line-through !important;
                }
                .ql-picker.ql-size .ql-picker-label[data-value]::before {
                    content: attr(data-value) !important;
                }
            `;
            document.head.appendChild(style);
            this.styleElement = style;
        },

        updateFontSizeSelector() {
            if (!this.quill || this.isDestroyed) return;

            let range = null;
            try {
                if (this.quill.getLength() <= 1) return;
                range = this.quill.getSelection();
            } catch (e) {
                return;
            }
            if (!range) return;

            let format = {};
            try {
                format = this.quill.getFormat(range) || {};
            } catch (e) {
                return;
            }

            let currentSize = this.defaultFontSize;

            if (format.size) {
                currentSize = format.size;
            } else if (format['font-size']) {
                currentSize = format['font-size'];
            } else if (format.header) {
                const sizes = {
                    1: '32px', 2: '28px', 3: '24px',
                    4: '20px', 5: '18px', 6: '16px'
                };
                currentSize = sizes[format.header] || this.defaultFontSize;
            }

            this.currentFontSize = currentSize;

            this.$nextTick(() => {
                if (this.isDestroyed) return;
                const sizePicker = document.querySelector('.ql-picker.ql-size');
                if (sizePicker) {
                    const label = sizePicker.querySelector('.ql-picker-label');
                    if (label) {
                        label.setAttribute('data-value', currentSize);

                        const existingSpan = label.querySelector('span');
                        if (existingSpan) {
                            existingSpan.remove();
                        }
                    }
                }
            });
        },

        enableInlineStyles() {
            if (!this.quill) return;

            const originalFormat = this.quill.format;

            this.quill.format = (name, value) => {
                if (!this.quill || this.isDestroyed) return;

                if (name === 'bold' && value) {
                    const range = this.safeGetSelection();
                    if (range) {
                        this.quill.formatText(range.index, range.length, {
                            'font-weight': 'bold'
                        });
                    }
                    return;
                }

                if (name === 'italic' && value) {
                    const range = this.safeGetSelection();
                    if (range) {
                        this.quill.formatText(range.index, range.length, {
                            'font-style': 'italic'
                        });
                    }
                    return;
                }

                if (name === 'underline' && value) {
                    const range = this.safeGetSelection();
                    if (range) {
                        this.quill.formatText(range.index, range.length, {
                            'text-decoration': 'underline'
                        });
                    }
                    return;
                }

                if (name === 'size' && value) {
                    const range = this.safeGetSelection();
                    if (range) {
                        this.quill.formatText(range.index, range.length, {
                            'font-size': value
                        });
                        this.$nextTick(() => {
                            this.safeUpdateFontSizeSelector();
                        });
                    }
                    return;
                }

                originalFormat.call(this.quill, name, value);
            };
        },

        safeGetSelection() {
            if (!this.quill || this.isDestroyed) return null;
            try {
                if (this.quill.getLength() <= 1) return null;
                return this.quill.getSelection();
            } catch (e) {
                return null;
            }
        },

        interceptFormatOperations() {
            if (!this.quill) return;

            const toolbar = this.quill.getModule('toolbar');
            if (!toolbar) return;

            toolbar.handlers.bold = () => {
                const range = this.safeGetSelection();
                if (range) {
                    const format = this.quill.getFormat(range);
                    const isBold = format.bold || format['font-weight'] === 'bold';

                    if (isBold) {
                        this.quill.formatText(range.index, range.length, {
                            'bold': false,
                            'font-weight': 'normal'
                        });
                    } else {
                        this.quill.formatText(range.index, range.length, {
                            'bold': true,
                            'font-weight': 'bold'
                        });
                    }
                    this.safeUpdateFontSizeSelector();
                }
            };

            toolbar.handlers.italic = () => {
                const range = this.safeGetSelection();
                if (range) {
                    const format = this.quill.getFormat(range);
                    const isItalic = format.italic || format['font-style'] === 'italic';

                    if (isItalic) {
                        this.quill.formatText(range.index, range.length, {
                            'italic': false,
                            'font-style': 'normal'
                        });
                    } else {
                        this.quill.formatText(range.index, range.length, {
                            'italic': true,
                            'font-style': 'italic'
                        });
                    }
                    this.safeUpdateFontSizeSelector();
                }
            };

            toolbar.handlers.underline = () => {
                const range = this.safeGetSelection();
                if (range) {
                    const format = this.quill.getFormat(range);
                    const isUnderline = format.underline ||
                        (format['text-decoration'] && format['text-decoration'].includes('underline'));

                    if (isUnderline) {
                        this.quill.formatText(range.index, range.length, {
                            'underline': false,
                            'text-decoration': 'none'
                        });
                    } else {
                        this.quill.formatText(range.index, range.length, {
                            'underline': true,
                            'text-decoration': 'underline'
                        });
                    }
                    this.safeUpdateFontSizeSelector();
                }
            };

            toolbar.handlers.size = (value) => {
                const range = this.safeGetSelection();
                if (range && value) {
                    this.quill.formatText(range.index, range.length, {
                        'font-size': value
                    });
                    this.currentFontSize = value;
                    this.safeUpdateFontSizeSelector();
                }
            };
        },

        setContent(content) {
            if (!this.quill || this.isDestroyed) return;

            if (!content) {
                this.quill.setText('');
                this.content = '';
                this.charCount = 0;
                this.applyDefaultStyles();
                return;
            }

            try {
                if (this.isHTML(content)) {
                    this.quill.root.innerHTML = content;

                    if (this.useInlineStyles) {
                        this.ensureInlineStyles();
                    }
                } else {
                    const paragraphs = content.split('\n').filter(p => p.trim());
                    this.quill.setText('');

                    if (paragraphs.length > 0) {
                        paragraphs.forEach((para, index) => {
                            if (index > 0) {
                                this.quill.insertText(this.quill.getLength(), '\n', {
                                    'font-size': this.defaultFontSize
                                });
                            }

                            const lengthBefore = this.quill.getLength();
                            this.quill.insertText(this.quill.getLength(), para, {
                                'font-size': this.defaultFontSize
                            });

                            this.quill.formatText(lengthBefore, para.length, {
                                'font-size': this.defaultFontSize
                            });
                        });
                    } else {
                        this.quill.setText(content);
                        this.applyDefaultStyles();
                    }
                }
            } catch (e) {
                console.warn('[RichTextEditor] Error al setear contenido:', e?.message);
                return;
            }

            this.updateContent();
            this.safeUpdateFontSizeSelector();
        },

        ensureInlineStyles() {
            if (!this.quill || this.isDestroyed) return;
            const editor = this.quill.root;
            const elements = editor.querySelectorAll('*');

            elements.forEach(element => {
                if (element.tagName === 'STRONG' || element.tagName === 'B') {
                    element.style.fontWeight = 'bold';
                }

                if (element.tagName === 'EM' || element.tagName === 'I') {
                    element.style.fontStyle = 'italic';
                }

                if (element.tagName === 'U') {
                    element.style.textDecoration = 'underline';
                }

                if (element.tagName === 'S' || element.tagName === 'STRIKE') {
                    element.style.textDecoration = 'line-through';
                }

                if (element.tagName === 'P' && !element.style.fontSize) {
                    element.style.fontSize = this.defaultFontSize;
                }

                if (element.tagName.match(/^H[1-6]$/)) {
                    const level = parseInt(element.tagName.charAt(1));
                    const sizes = {
                        1: '32px', 2: '28px', 3: '24px',
                        4: '20px', 5: '18px', 6: '16px'
                    };
                    element.style.fontSize = sizes[level] || this.defaultFontSize;
                    element.style.fontWeight = 'bold';
                }
            });
        },

        applyDefaultStyles() {
            if (!this.quill || this.isDestroyed) return;
            const length = this.quill.getLength();
            if (length > 0) {
                this.quill.formatText(0, length, {
                    'font-size': this.defaultFontSize
                });
            }
        },

        isHTML(str) {
            if (typeof str !== 'string') return false;
            const htmlRegex = /<[a-z][\s\S]*>/i;
            return htmlRegex.test(str);
        },

        updateContent() {
            if (!this.quill || this.isDestroyed) return;
            const html = this.getHTMLWithStyles();
            const text = this.quill.getText();
            if (html !== this.content) {
                this.content = html;
                this.$emit('input', html);
                this.$emit('change', {
                    html: html,
                    text: text,
                    delta: this.quill.getContents()
                });
            }
            this.updateCharCount();
        },

        getHTMLWithStyles() {
            if (!this.quill || this.isDestroyed) return '';

            let html = this.quill.root.innerHTML;

            if (this.useInlineStyles) {
                html = this.addInlineStyles(html);
            }

            return html;
        },

        addInlineStyles(html) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const processElement = (element) => {
                Array.from(element.children).forEach(processElement);

                if (element.tagName === 'STRONG' || element.tagName === 'B') {
                    element.style.fontWeight = 'bold';
                    if (this.useInlineStyles) {
                        const span = document.createElement('span');
                        span.innerHTML = element.innerHTML;
                        span.style.fontWeight = 'bold';
                        element.parentNode.replaceChild(span, element);
                    }
                }

                if (element.tagName === 'EM' || element.tagName === 'I') {
                    element.style.fontStyle = 'italic';
                }

                if (element.tagName === 'U') {
                    element.style.textDecoration = 'underline';
                }

                const fontSize = element.style.fontSize ||
                    element.getAttribute('data-font-size') ||
                    (element.tagName.match(/^H[1-6]$/) ?
                        this.getHeaderSize(element.tagName) : null);

                if (fontSize && !element.style.fontSize) {
                    element.style.fontSize = fontSize;
                }

                if (!element.style.fontSize &&
                    ['P', 'DIV', 'SPAN'].includes(element.tagName) &&
                    element.children.length === 0) {
                    element.style.fontSize = this.defaultFontSize;
                }
            };

            Array.from(doc.body.children).forEach(processElement);

            return doc.body?.innerHTML;
        },

        getHeaderSize(tagName) {
            const sizes = {
                'H1': '32px',
                'H2': '28px',
                'H3': '24px',
                'H4': '20px',
                'H5': '18px',
                'H6': '16px'
            };
            return sizes[tagName] || this.defaultFontSize;
        },

        updateCharCount() {
            if (!this.quill || this.isDestroyed) return;
            const text = this.quill.getText();
            this.charCount = text.length;
            this.$emit('char-count', this.charCount);
        },

        getHTML() {
            return this.getHTMLWithStyles();
        },

        getText() {
            if (!this.quill || this.isDestroyed) return '';
            return this.quill.getText();
        },

        getDelta() {
            if (!this.quill || this.isDestroyed) return null;
            return this.quill.getContents();
        },

        clear() {
            if (!this.quill || this.isDestroyed) return;
            this.quill.setText('');
            this.updateContent();
        },

        formatBold() {
            const range = this.safeGetSelection();
            if (range) {
                this.quill.formatText(range.index, range.length, {
                    'font-weight': 'bold'
                });
                this.safeUpdateFontSizeSelector();
            }
        },

        formatItalic() {
            const range = this.safeGetSelection();
            if (range) {
                this.quill.formatText(range.index, range.length, {
                    'font-style': 'italic'
                });
                this.safeUpdateFontSizeSelector();
            }
        },

        formatUnderline() {
            const range = this.safeGetSelection();
            if (range) {
                this.quill.formatText(range.index, range.length, {
                    'text-decoration': 'underline'
                });
                this.safeUpdateFontSizeSelector();
            }
        },

        setFontSize(size) {
            const range = this.safeGetSelection();
            if (range) {
                const fontSize = typeof size === 'number' ? `${size}px` : size;
                this.quill.formatText(range.index, range.length, {
                    'font-size': fontSize
                });
                this.currentFontSize = fontSize;
                this.safeUpdateFontSizeSelector();
            }
        },

        setHeader(level) {
            const range = this.safeGetSelection();
            if (range) {
                this.quill.formatText(range.index, range.length, 'header', false);

                this.quill.formatText(range.index, range.length, 'header', level);

                const sizes = {
                    1: '32px', 2: '28px', 3: '24px',
                    4: '20px', 5: '18px', 6: '16px'
                };
                if (sizes[level]) {
                    this.quill.formatText(range.index, range.length, {
                        'font-size': sizes[level],
                        'font-weight': 'bold'
                    });
                }
                this.safeUpdateFontSizeSelector();
            }
        },

        getStyledContent() {
            return {
                html: this.getHTMLWithStyles(),
                text: this.getText(),
                delta: this.getDelta()
            };
        },

        // Método público seguro para setear HTML desde el padre (Notas.vue)
        setHTML(html) {
            if (!this.quill || this.isDestroyed) return;
            try {
                if (!html) {
                    this.quill.setText('');
                    this.updateContent();
                    return;
                }
                if (this.isHTML(html)) {
                    this.quill.root.innerHTML = html;
                    if (this.useInlineStyles) {
                        this.ensureInlineStyles();
                    }
                } else {
                    this.quill.setText(html);
                }
                this.updateContent();
                this.safeUpdateFontSizeSelector();
            } catch (e) {
                console.warn('[RichTextEditor] setHTML fallo:', e?.message);
            }
        }
    }
};
</script>
<style scoped>
.rich-text-editor-container {
    border: 1px solid #ccc;
    border-radius: 4px;
    overflow: hidden;
}
.quill-editor {
    background: white;
}
.char-counter {
    padding: 5px 10px;
    background: #f5f5f5;
    border-top: 1px solid #ddd;
    font-size: 12px;
    text-align: right;
    color: #666;
}
.char-counter.near-limit {
    background: #fff3cd;
    color: #856404;
}
.char-counter .warning {
    color: #ff9800;
    font-weight: bold;
}
.char-counter .error {
    color: #f44336;
    font-weight: bold;
}
:deep(.ql-toolbar.ql-snow) {
    border: none;
    border-bottom: 1px solid #ddd;
    padding: 8px;
}
:deep(.ql-container.ql-snow) {
    border: none;
    font-size: 14px;
}
:deep(.ql-editor) {
    min-height: 300px;
    padding: 15px;
    line-height: 1.6;
}
:deep(.ql-editor.ql-blank::before) {
    font-style: normal;
    color: #999;
}
:deep(.ql-toolbar button:hover),
:deep(.ql-toolbar button:focus),
:deep(.ql-toolbar button.ql-active) {
    color: #1976d2;
}
:deep(.ql-toolbar .ql-picker-item:hover) {
    color: #1976d2;
}
:deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value])::before {
    content: attr(data-value) !important;
}
:deep(.ql-picker.ql-size .ql-picker-label) {
    display: flex;
    align-items: center;
}
</style>